import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from './components/AppLayout';
import { RequireAuth, RequireGuest } from './components/RequireAuth';
import { OnboardingGate, RedirectIfOnboarded } from './components/OnboardingGate';
import { NotFoundPage } from './components/NotFoundPage';
import { Spinner } from './components/ui';

const LoginPage = lazy(() => import('./features/auth/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./features/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() =>
  import('./features/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const GoogleSuccessPage = lazy(() =>
  import('./features/auth/CallbackPages').then((m) => ({ default: m.GoogleSuccessPage })),
);
const GoogleErrorPage = lazy(() => import('./features/auth/CallbackPages').then((m) => ({ default: m.GoogleErrorPage })));
const EmailVerifiedPage = lazy(() =>
  import('./features/auth/CallbackPages').then((m) => ({ default: m.EmailVerifiedPage })),
);
const OnboardingPage = lazy(() => import('./features/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage })));
const FeedPage = lazy(() => import('./features/tenders/FeedPage').then((m) => ({ default: m.FeedPage })));
const TenderDetailPage = lazy(() =>
  import('./features/tenders/TenderDetailPage').then((m) => ({ default: m.TenderDetailPage })),
);
const FiltersListPage = lazy(() =>
  import('./features/filters/FiltersListPage').then((m) => ({ default: m.FiltersListPage })),
);
const FilterFormPage = lazy(() => import('./features/filters/FilterFormPage').then((m) => ({ default: m.FilterFormPage })));
const NotificationsPage = lazy(() =>
  import('./features/notifications/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
);
const SubscriptionPage = lazy(() =>
  import('./features/subscription/SubscriptionPage').then((m) => ({ default: m.SubscriptionPage })),
);
const CheckoutPage = lazy(() => import('./features/subscription/CheckoutPage').then((m) => ({ default: m.CheckoutPage })));
const InvoicesPage = lazy(() => import('./features/subscription/InvoicesPage').then((m) => ({ default: m.InvoicesPage })));
const InvoiceDetailPage = lazy(() =>
  import('./features/subscription/InvoiceDetailPage').then((m) => ({ default: m.InvoiceDetailPage })),
);
const AccountPage = lazy(() => import('./features/account/AccountPage').then((m) => ({ default: m.AccountPage })));
const InstallPage = lazy(() => import('./features/install/InstallPage').then((m) => ({ default: m.InstallPage })));
const StatsPage = lazy(() => import('./features/stats/StatsPage').then((m) => ({ default: m.StatsPage })));

function PageFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner label="Carregando página" />
    </div>
  );
}

export function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route element={<RequireGuest />}>
          <Route path="/entrar" element={<LoginPage />} />
          <Route path="/cadastro" element={<RegisterPage />} />
          <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
          <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
          <Route path="/auth/google/erro" element={<GoogleErrorPage />} />
        </Route>

        <Route element={<RequireAuth />}>
          <Route path="/auth/google/sucesso" element={<GoogleSuccessPage />} />
          <Route path="/email-verificado" element={<EmailVerifiedPage />} />

          <Route element={<RedirectIfOnboarded />}>
            <Route path="/onboarding" element={<OnboardingPage />} />
          </Route>

          <Route element={<OnboardingGate />}>
            <Route element={<AppLayout />}>
              <Route path="/" element={<Navigate to="/licitacoes" replace />} />
              <Route path="/licitacoes" element={<FeedPage />} />
              <Route path="/licitacoes/:id" element={<TenderDetailPage />} />
              <Route path="/filtros" element={<FiltersListPage />} />
              <Route path="/filtros/novo" element={<FilterFormPage />} />
              <Route path="/filtros/:id" element={<FilterFormPage />} />
              <Route path="/notificacoes" element={<NotificationsPage />} />
              <Route path="/estatisticas" element={<StatsPage />} />
              <Route path="/assinatura" element={<SubscriptionPage />} />
              <Route path="/assinatura/checkout/:planSlug" element={<CheckoutPage />} />
              <Route path="/faturas" element={<InvoicesPage />} />
              <Route path="/faturas/:id" element={<InvoiceDetailPage />} />
              <Route path="/conta" element={<AccountPage />} />
            </Route>
          </Route>
        </Route>

        {/* Public: the site's "Instalar o app" button lands here, logged in or not (ADR-0013). */}
        <Route path="/instalar" element={<InstallPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
